import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";

import {
  collection,
  doc,
  getDocs,
  query,
  where,
  serverTimestamp,
  runTransaction,
  onSnapshot,
} from "firebase/firestore";

import { db } from "../firebase";
import "./Volatility.css";
import VolatilityHeader from "./VolatilityHeader";
import BottomNavVolatility from "./BottomNavVolatility";
import ForexWebSocket from "../services/ForexWebSocket";

/* =========================================================
   REAL DERIV MARKETS
========================================================= */

const MARKETS = [
  {
    id: "volatility10",
    symbol: "R_10",
    name: "Volatility 10 Index",
    fallbackDecimals: 2,
  },
  {
    id: "volatility25",
    symbol: "R_25",
    name: "Volatility 25 Index",
    fallbackDecimals: 2,
  },
  {
    id: "volatility50",
    symbol: "R_50",
    name: "Volatility 50 Index",
    fallbackDecimals: 2,
  },
  {
    id: "volatility75",
    symbol: "R_75",
    name: "Volatility 75 Index",
    fallbackDecimals: 2,
  },
  {
    id: "volatility100",
    symbol: "R_100",
    name: "Volatility 100 Index",
    fallbackDecimals: 2,
  },
  {
    id: "volatility10-1s",
    symbol: "1HZ10V",
    name: "Volatility 10 (1s) Index",
    fallbackDecimals: 2,
  },
  {
    id: "volatility25-1s",
    symbol: "1HZ25V",
    name: "Volatility 25 (1s) Index",
    fallbackDecimals: 2,
  },
  {
    id: "volatility50-1s",
    symbol: "1HZ50V",
    name: "Volatility 50 (1s) Index",
    fallbackDecimals: 2,
  },
  {
    id: "volatility75-1s",
    symbol: "1HZ75V",
    name: "Volatility 75 (1s) Index",
    fallbackDecimals: 2,
  },
  {
    id: "volatility100-1s",
    symbol: "1HZ100V",
    name: "Volatility 100 (1s) Index",
    fallbackDecimals: 2,
  },
];

/* =========================================================
   DIGITS
========================================================= */

const DIGITS = [
  { number: 0, percent: "11.7%" },
  { number: 1, percent: "6.7%" },
  { number: 2, percent: "5.0%" },
  { number: 3, percent: "10.0%" },
  { number: 4, percent: "3.3%" },
  { number: 5, percent: "13.3%" },
  { number: 6, percent: "8.3%" },
  { number: 7, percent: "10.0%" },
  { number: 8, percent: "16.7%" },
  { number: 9, percent: "15.0%" },
];

/* =========================================================
   AMOUNT PRESETS
========================================================= */

const AMOUNT_PRESETS = [1, 5, 10, 25, 50, 100];

/* =========================================================
   CONTRACTS
========================================================= */

const CONTRACTS = [
  {
    id: "rise-fall",
    label: "Rise/Fall",
  },
  {
    id: "matches-differs",
    label: "Matches/Differs",
  },
  {
    id: "even-odd",
    label: "Even/Odd",
  },
  {
    id: "over-under",
    label: "Over/Under",
  },
];

/* =========================================================
   HELPERS
========================================================= */

const getTimestampValue = (value) => {
  if (!value) return 0;

  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  const parsed = new Date(value).getTime();

  return Number.isFinite(parsed) ? parsed : 0;
};

const getDecimalPlaces = (
  pipSize,
  fallback = 2
) => {
  if (
    pipSize === null ||
    pipSize === undefined
  ) {
    return fallback;
  }

  const numeric = Number(pipSize);

  if (!Number.isFinite(numeric)) {
    return fallback;
  }

  /*
   * Deriv pip_size mara nyingi huwa:
   * 0, 1, 2, 3, 4...
   *
   * Lakini baadhi ya integrations
   * zinaweza kutuma decimal.
   */
  if (
    Number.isInteger(numeric) &&
    numeric >= 0 &&
    numeric <= 10
  ) {
    return numeric;
  }

  const text = String(numeric);

  if (text.includes(".")) {
    const decimals =
      text.split(".")[1]?.length || 0;

    if (
      decimals > 0 &&
      decimals <= 10
    ) {
      return decimals;
    }
  }

  return fallback;
};

const getLastDigit = (
  value,
  pipSize,
  fallbackDecimals = 2
) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return 0;
  }

  const decimals = getDecimalPlaces(
    pipSize,
    fallbackDecimals
  );

  const fixedPrice =
    number.toFixed(decimals);

  const digitsOnly =
    fixedPrice.replace(/\D/g, "");

  if (!digitsOnly) {
    return 0;
  }

  const digit = Number(
    digitsOnly.charAt(
      digitsOnly.length - 1
    )
  );

  return Number.isFinite(digit)
    ? digit
    : 0;
};

const formatMarketPrice = (
  value,
  pipSize,
  fallbackDecimals = 2
) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "—";
  }

  const decimals =
    getDecimalPlaces(
      pipSize,
      fallbackDecimals
    );

  return number.toLocaleString(
    "en-US",
    {
      minimumFractionDigits:
        decimals,
      maximumFractionDigits:
        decimals,
    }
  );
};

const formatTickTime = (epoch) => {
  if (!epoch) return "";

  const date = new Date(
    Number(epoch) * 1000
  );

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleTimeString(
    [],
    {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }
  );
};

const getDerivErrorMessage = (
  data
) => {
  return (
    data?.error?.message ||
    data?.error?.code ||
    "Deriv WebSocket error."
  );
};

/* =========================================================
   ANIMATED MONEY
========================================================= */

const AnimatedMoney = ({
  value = 0,
  suffix = "USD",
}) => {
  const target = Number(value) || 0;

  const [displayValue, setDisplayValue] =
    useState(target);

  const animationRef =
    useRef(null);

  const previousValueRef =
    useRef(target);

  useEffect(() => {
    const from =
      Number(
        previousValueRef.current
      ) || 0;

    const to =
      Number(target) || 0;

    if (from === to) {
      setDisplayValue(to);
      return undefined;
    }

    if (animationRef.current) {
      cancelAnimationFrame(
        animationRef.current
      );
    }

    const duration = 280;
    const startTime =
      performance.now();

    const animate = (
      currentTime
    ) => {
      const elapsed =
        currentTime -
        startTime;

      const progress =
        Math.min(
          elapsed / duration,
          1
        );

      const eased =
        1 -
        Math.pow(
          1 - progress,
          3
        );

      const nextValue =
        from +
        (to - from) *
          eased;

      setDisplayValue(
        nextValue
      );

      if (progress < 1) {
        animationRef.current =
          requestAnimationFrame(
            animate
          );
      } else {
        previousValueRef.current =
          to;

        setDisplayValue(to);
      }
    };

    animationRef.current =
      requestAnimationFrame(
        animate
      );

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(
          animationRef.current
        );
      }
    };
  }, [target]);

  return (
    <span className="trade-payout-value">
      {displayValue.toFixed(2)}{" "}
      {suffix}
    </span>
  );
};

/* =========================================================
   VOLATILITY
========================================================= */

const Volatility = () => {
  /* =======================================================
     REFS
  ======================================================= */

  const mountedRef =
    useRef(false);

  const selectedMarketRef =
    useRef(
      "volatility10-1s"
    );

  const historyRequestRef =
    useRef(null);

  const previousLivePriceRef =
    useRef(null);

  const liveMarketsRef =
    useRef({});

  /* =======================================================
     USER / TRADER
  ======================================================= */

  const [traderTab, setTraderTab] =
    useState("manual");

  const [contractType, setContractType] =
    useState("even-odd");

  /* =======================================================
     MARKET
  ======================================================= */

  const [
    selectedMarketId,
    setSelectedMarketId,
  ] = useState(
    "volatility10-1s"
  );

  const [marketOpen, setMarketOpen] =
    useState(false);

  /* =======================================================
     NAV
  ======================================================= */

  const [volatilityNav, setVolatilityNav] =
    useState("history");

  const [historyTab, setHistoryTab] =
    useState("open");

  /* =======================================================
     DERIV
  ======================================================= */

  const [wsStatus, setWsStatus] =
    useState("connecting");

  const [wsError, setWsError] =
    useState("");

  const [liveMarkets, setLiveMarkets] =
    useState({});

  const [activeSymbols, setActiveSymbols] =
    useState({});

  /* =======================================================
     CHART
  ======================================================= */

  const [chartLoading, setChartLoading] =
    useState(true);

  const [chartData, setChartData] =
    useState([]);

  /* =======================================================
     PRICE
  ======================================================= */

  const [price, setPrice] =
    useState(null);

  const [previousPrice, setPreviousPrice] =
    useState(null);

  const [pricePipSize, setPricePipSize] =
    useState(2);

  /* =======================================================
     TICK
  ======================================================= */

  const [
    currentTickEpoch,
    setCurrentTickEpoch,
  ] = useState(null);

  const [
    currentTickId,
    setCurrentTickId,
  ] = useState(null);

  /* =======================================================
     SESSION
  ======================================================= */

  const [
    sessionStartPrice,
    setSessionStartPrice,
  ] = useState(null);

  /* =======================================================
     TRADING
  ======================================================= */

  const [tradingMode, setTradingMode] =
    useState("manual");

  const [amount, setAmount] =
    useState(0);

  const [currentStake, setCurrentStake] =
    useState(1);

  const [targetProfit, setTargetProfit] =
    useState(20);

  const [stopLoss, setStopLoss] =
    useState(50);

  const [multiplier, setMultiplier] =
    useState(2);

  const [resetOnWin, setResetOnWin] =
    useState(true);

  const [onWinReset, setOnWinReset] =
    useState(1);

  const [maxTrades, setMaxTrades] =
    useState(50);

  const [isRunning, setIsRunning] =
    useState(false);

  const [tradeType, setTradeType] =
    useState(null);

  /* =======================================================
     USER
  ======================================================= */

  const [username, setUsername] =
    useState(() =>
      localStorage.getItem(
        "username"
      ) || ""
    );

  /* =======================================================
     BALANCE
  ======================================================= */

  const [balance, setBalance] =
    useState(0);

  const [balanceLoading, setBalanceLoading] =
    useState(true);

  /* =======================================================
     ORDERS
  ======================================================= */

  const [orders, setOrders] =
    useState([]);

  const [ordersLoading, setOrdersLoading] =
    useState(true);

  /* =======================================================
     SESSION STATS
  ======================================================= */

  const [tradeCount, setTradeCount] =
    useState(0);

  const [totalProfit, setTotalProfit] =
    useState(0);

  const [totalLoss, setTotalLoss] =
    useState(0);

  /* =======================================================
     SELECTED MARKET
  ======================================================= */

  const selectedMarket = useMemo(() => {
    return (
      MARKETS.find(
        (market) =>
          market.id ===
          selectedMarketId
      ) ||
      MARKETS[0]
    );
  }, [selectedMarketId]);

  /* =======================================================
     KEEP SELECTED MARKET REF UPDATED
  ======================================================= */

  useEffect(() => {
    selectedMarketRef.current =
      selectedMarketId;
  }, [selectedMarketId]);

  /* =======================================================
     LIVE MARKETS REF
  ======================================================= */

  useEffect(() => {
    liveMarketsRef.current =
      liveMarkets;
  }, [liveMarkets]);

  /* =======================================================
     SELECTED LIVE MARKET
  ======================================================= */

  const selectedLiveMarket =
    liveMarkets[
      selectedMarket.symbol
    ] || null;

  /* =======================================================
     DISPLAY PRICE
  ======================================================= */

  const displayedPrice =
    selectedLiveMarket?.price ??
    price;

  /* =======================================================
     DISPLAY STAKE
  ======================================================= */

  const displayStake =
    tradingMode === "auto" &&
    Number(currentStake) > 0
      ? Number(currentStake)
      : Number(amount);

  const activeStake =
    Number(displayStake) || 0;

  const activePayout =
    activeStake * 1.98;

  /* =======================================================
     SELECTED DIGIT
  ======================================================= */

  const selectedDigit = useMemo(() => {
    return getLastDigit(
      displayedPrice,
      pricePipSize,
      selectedMarket.fallbackDecimals
    );
  }, [
    displayedPrice,
    pricePipSize,
    selectedMarket,
  ]);

  /* =======================================================
     SESSION %
  ======================================================= */

  const marketSessionPercentage =
    useMemo(() => {
      if (
        sessionStartPrice ===
          null ||
        sessionStartPrice ===
          undefined ||
        Number(sessionStartPrice) ===
          0 ||
        displayedPrice === null ||
        displayedPrice ===
          undefined
      ) {
        return 0;
      }

      return (
        ((Number(displayedPrice) -
          Number(
            sessionStartPrice
          )) /
          Number(
            sessionStartPrice
          )) *
        100
      );
    }, [
      displayedPrice,
      sessionStartPrice,
    ]);

  /* =======================================================
     PRICE FORMAT
  ======================================================= */

  const formatPrice =
    useCallback(
      (value) => {
        return formatMarketPrice(
          value,
          pricePipSize,
          selectedMarket.fallbackDecimals
        );
      },
      [
        pricePipSize,
        selectedMarket,
      ]
    );

  const percentageText =
    marketSessionPercentage >= 0
      ? `▲ +${marketSessionPercentage.toFixed(
          2
        )}%`
      : `▼ ${marketSessionPercentage.toFixed(
          2
        )}%`;

  /* =======================================================
     MARKET VALUES

     IMPORTANT:
     sessionStartPrice ya kila market
     inawekwa kutoka history ya market hiyo.
  ======================================================= */

  const marketValues = useMemo(() => {
    return MARKETS.map(
      (market) => {
        const live =
          liveMarkets[
            market.symbol
          ];

        const displayPrice =
          live?.price ?? null;

        const referencePrice =
          live?.sessionStartPrice ??
          null;

        let displayChange = 0;

        if (
          displayPrice !== null &&
          referencePrice !== null &&
          Number(referencePrice) !==
            0
        ) {
          displayChange =
            ((Number(displayPrice) -
              Number(
                referencePrice
              )) /
              Number(
                referencePrice
              )) *
            100;
        }

        return {
          ...market,

          displayPrice,

          displayChange:
            Number(
              displayChange.toFixed(
                2
              )
            ),

          pipSize:
            live?.pipSize ??
            market.fallbackDecimals,

          referencePrice,
        };
      }
    );
  }, [liveMarkets]);

  /* =======================================================
     USERNAME
  ======================================================= */

  const getCurrentUsername =
    useCallback(() => {
      const stored =
        localStorage.getItem(
          "username"
        );

      return stored
        ? stored.trim()
        : "";
    }, []);

  /* =======================================================
     GET USER ACCOUNT
  ======================================================= */

  const getUserAccount =
    useCallback(async () => {
      const currentUsername =
        getCurrentUsername();

      if (!currentUsername) {
        return null;
      }

      const usersRef =
        collection(
          db,
          "users"
        );

      const userQuery =
        query(
          usersRef,
          where(
            "username",
            "==",
            currentUsername
          )
        );

      const snapshot =
        await getDocs(
          userQuery
        );

      if (snapshot.empty) {
        return null;
      }

      const userDocument =
        snapshot.docs[0];

      const userData =
        userDocument.data();

      const userBalance =
        Number(
          userData
            .usdtBalanceOther
        ) || 0;

      return {
        id: userDocument.id,
        username:
          currentUsername,
        balance:
          userBalance,
        data: userData,
      };
    }, [getCurrentUsername]);

  /* =======================================================
     LOAD BALANCE
  ======================================================= */

  const loadUserBalance =
    useCallback(async () => {
      try {
        setBalanceLoading(true);

        const user =
          await getUserAccount();

        if (!user) {
          setBalance(0);
          return null;
        }

        setUsername(
          user.username
        );

        setBalance(
          user.balance
        );

        return user;
      } catch (error) {
        console.error(
          "Failed to load user balance:",
          error
        );

        return null;
      } finally {
        setBalanceLoading(false);
      }
    }, [getUserAccount]);

  useEffect(() => {
    loadUserBalance();
  }, [loadUserBalance]);

  /* =======================================================
     FIRESTORE ORDERS
  ======================================================= */

  useEffect(() => {
    const currentUsername =
      getCurrentUsername();

    if (!currentUsername) {
      setOrders([]);
      setOrdersLoading(false);
      return undefined;
    }

    setOrdersLoading(true);

    const ordersRef =
      collection(
        db,
        "volatilityorder"
      );

    const ordersQuery =
      query(
        ordersRef,
        where(
          "username",
          "==",
          currentUsername
        )
      );

    const unsubscribe =
      onSnapshot(
        ordersQuery,
        (snapshot) => {
          const loadedOrders =
            snapshot.docs.map(
              (document) => ({
                id: document.id,
                firestoreOrderId:
                  document.id,
                ...document.data(),
              })
            );

          loadedOrders.sort(
            (a, b) =>
              getTimestampValue(
                b.closedAt ||
                  b.updatedAt ||
                  b.createdAt
              ) -
              getTimestampValue(
                a.closedAt ||
                  a.updatedAt ||
                  a.createdAt
              )
          );

          setOrders(
            loadedOrders
          );

          setOrdersLoading(
            false
          );
        },
        (error) => {
          console.error(
            "Volatility orders listener error:",
            error
          );

          setOrders([]);
          setOrdersLoading(
            false
          );
        }
      );

    return () => {
      unsubscribe();
    };
  }, [getCurrentUsername]);

  /* =======================================================
     OPEN TRADES
  ======================================================= */

  const openTrades = useMemo(() => {
    return orders
      .filter(
        (order) =>
          order.status ===
          "OPEN"
      )
      .sort(
        (a, b) =>
          getTimestampValue(
            b.createdAt
          ) -
          getTimestampValue(
            a.createdAt
          )
      );
  }, [orders]);

  /* =======================================================
     CLOSED TRADES
  ======================================================= */

  const closedTrades =
    useMemo(() => {
      return orders
        .filter(
          (order) =>
            order.status ===
              "WON" ||
            order.status ===
              "LOST"
        )
        .sort(
          (a, b) =>
            getTimestampValue(
              b.closedAt ||
                b.updatedAt ||
                b.createdAt
            ) -
            getTimestampValue(
              a.closedAt ||
                a.updatedAt ||
                a.createdAt
            )
        );
    }, [orders]);

  /* =======================================================
     TRANSACTIONS
  ======================================================= */

  const transactions =
    useMemo(() => {
      return [...orders].sort(
        (a, b) =>
          getTimestampValue(
            b.closedAt ||
              b.updatedAt ||
              b.createdAt
          ) -
          getTimestampValue(
            a.closedAt ||
              a.updatedAt ||
              a.createdAt
          )
      );
    }, [orders]);

  /* =======================================================
     DERIV CONNECTION

     ONE SOCKET.
     ALL MARKETS SUBSCRIBED.
  ======================================================= */

  useEffect(() => {
    mountedRef.current = true;

    setWsStatus(
      "connecting"
    );

    setWsError("");

    ForexWebSocket.connect();

    const unsubscribe =
      ForexWebSocket.onTick(
        (data) => {
          if (!mountedRef.current) {
            return;
          }

          /* -----------------------------------------------
             CONNECTION
          ----------------------------------------------- */

          if (
            data?.type ===
            "connection"
          ) {
            if (
              data.connected
            ) {
              setWsStatus(
                "connected"
              );

              setWsError("");
            } else {
              setWsStatus(
                "disconnected"
              );
            }

            return;
          }

          /* -----------------------------------------------
             CONNECTION ERROR
          ----------------------------------------------- */

          if (
            data?.type ===
            "connection_error"
          ) {
            setWsError(
              data.error ||
                "Deriv connection error."
            );

            return;
          }

          /* -----------------------------------------------
             API ERROR

             IMPORTANT:
             API request error si socket OFFLINE.
          ----------------------------------------------- */

          if (data?.error) {
            console.error(
              "Deriv API error:",
              data
            );

            setWsError(
              getDerivErrorMessage(
                data
              )
            );

            if (
              ForexWebSocket.isConnected()
            ) {
              setWsStatus(
                "connected"
              );
            }

            return;
          }

          /* -----------------------------------------------
             ACTIVE SYMBOLS
          ----------------------------------------------- */

          if (
            data?.msg_type ===
              "active_symbols" &&
            Array.isArray(
              data.active_symbols
            )
          ) {
            const symbolMap = {};

            data.active_symbols.forEach(
              (item) => {
                const symbol =
                  item.symbol ||
                  item.underlying_symbol;

                if (!symbol) {
                  return;
                }

                symbolMap[
                  symbol
                ] = {
                  ...item,
                  symbol,
                  displayName:
                    item.display_name ||
                    item.underlying_symbol_name ||
                    symbol,
                };
              }
            );

            setActiveSymbols(
              symbolMap
            );

            return;
          }

          /* -----------------------------------------------
             LIVE TICK
          ----------------------------------------------- */

          if (
            data?.msg_type ===
              "tick" &&
            data.tick
          ) {
            const tick =
              data.tick;

            const symbol =
              tick.symbol;

            const quote =
              Number(
                tick.quote
              );

            if (
              !symbol ||
              !Number.isFinite(
                quote
              )
            ) {
              return;
            }

            const epoch =
              Number(
                tick.epoch
              ) || null;

            const tickId =
              tick.id || null;

            const pipSize =
              tick.pip_size ??
              null;

            /* ---------------------------------------------
               SAVE LIVE MARKET
            --------------------------------------------- */

            setLiveMarkets(
              (current) => {
                const previous =
                  current[
                    symbol
                  ];

                const sessionStart =
                  previous?.sessionStartPrice ??
                  quote;

                return {
                  ...current,

                  [symbol]: {
                    price: quote,

                    epoch,

                    pipSize,

                    tickId,

                    sessionStartPrice:
                      sessionStart,
                  },
                };
              }
            );

            /* ---------------------------------------------
               SELECTED MARKET
            --------------------------------------------- */

            const currentSelectedMarket =
              MARKETS.find(
                (market) =>
                  market.id ===
                  selectedMarketRef.current
              ) ||
              MARKETS[0];

            if (
              symbol ===
              currentSelectedMarket.symbol
            ) {
              const previous =
                previousLivePriceRef.current;

              if (
                previous !==
                  null &&
                Number.isFinite(
                  Number(
                    previous
                  )
                )
              ) {
                setPreviousPrice(
                  Number(
                    previous
                  )
                );
              }

              previousLivePriceRef.current =
                quote;

              setPrice(
                quote
              );

              if (
                pipSize !==
                  null &&
                pipSize !==
                  undefined
              ) {
                setPricePipSize(
                  getDecimalPlaces(
                    pipSize,
                    currentSelectedMarket.fallbackDecimals
                  )
                );
              }

              setCurrentTickEpoch(
                epoch
              );

              setCurrentTickId(
                tickId
              );

              setChartData(
                (currentData) => {
                  const next = [
                    ...currentData,

                    {
                      time:
                        epoch ||
                        Date.now(),

                      epoch,

                      price:
                        quote,
                    },
                  ];

                  return next.slice(
                    -100
                  );
                }
              );

              setChartLoading(
                false
              );
            }

            return;
          }

          /* -----------------------------------------------
             HISTORY
          ----------------------------------------------- */

          if (
            data?.msg_type ===
              "history" &&
            data.history
          ) {
            const requestId =
              Number(
                data.req_id
              );

            const expectedRequestId =
              Number(
                historyRequestRef.current
              );

            if (
              expectedRequestId &&
              requestId &&
              requestId !==
                expectedRequestId
            ) {
              return;
            }

            const currentSelectedMarket =
              MARKETS.find(
                (market) =>
                  market.id ===
                  selectedMarketRef.current
              ) ||
              MARKETS[0];

            const prices =
              Array.isArray(
                data.history
                  .prices
              )
                ? data.history
                    .prices
                : [];

            const times =
              Array.isArray(
                data.history
                  .times
              )
                ? data.history
                    .times
                : [];

            if (
              prices.length ===
              0
            ) {
              setChartLoading(
                false
              );

              return;
            }

            const historyData =
              prices
                .map(
                  (
                    historicalPrice,
                    index
                  ) => ({
                    time:
                      Number(
                        times[index]
                      ) ||
                      index,

                    epoch:
                      Number(
                        times[index]
                      ) ||
                      null,

                    price:
                      Number(
                        historicalPrice
                      ),
                  })
                )
                .filter(
                  (item) =>
                    Number.isFinite(
                      item.price
                    )
                );

            if (
              historyData.length >
              0
            ) {
              const first =
                historyData[0];

              const last =
                historyData[
                  historyData.length -
                    1
                ];

              /* -------------------------------------------
                 CHART
              ------------------------------------------- */

              setChartData(
                historyData.slice(
                  -100
                )
              );

              /* -------------------------------------------
                 REFERENCE PRICE

                 Hii ndiyo reference ya
                 percentage ya market.
              ------------------------------------------- */

              setSessionStartPrice(
                first.price
              );

              /* -------------------------------------------
                 CURRENT PRICE
              ------------------------------------------- */

              setPrice(
                last.price
              );

              setPreviousPrice(
                first.price
              );

              previousLivePriceRef.current =
                last.price;

              /* -------------------------------------------
                 SAVE HISTORY REFERENCE
                 INSIDE THAT MARKET
              ------------------------------------------- */

              setLiveMarkets(
                (current) => {
                  const existing =
                    current[
                      currentSelectedMarket
                        .symbol
                    ];

                  return {
                    ...current,

                    [
                      currentSelectedMarket
                        .symbol
                    ]: {
                      ...(existing ||
                        {}),

                      price:
                        existing?.price ??
                        last.price,

                      epoch:
                        existing?.epoch ??
                        last.epoch ??
                        null,

                      pipSize:
                        data.pip_size ??
                        existing?.pipSize ??
                        null,

                      tickId:
                        existing?.tickId ??
                        null,

                      sessionStartPrice:
                        first.price,
                    },
                  };
                }
              );

              /* -------------------------------------------
                 PIP SIZE
              ------------------------------------------- */

              if (
                data.pip_size !==
                  undefined &&
                data.pip_size !==
                  null
              ) {
                setPricePipSize(
                  getDecimalPlaces(
                    data.pip_size,
                    currentSelectedMarket.fallbackDecimals
                  )
                );
              } else {
                const currentLive =
                  liveMarketsRef.current[
                    currentSelectedMarket
                      .symbol
                  ];

                if (
                  currentLive?.pipSize !==
                    undefined &&
                  currentLive?.pipSize !==
                    null
                ) {
                  setPricePipSize(
                    getDecimalPlaces(
                      currentLive.pipSize,
                      currentSelectedMarket.fallbackDecimals
                    )
                  );
                } else {
                  setPricePipSize(
                    currentSelectedMarket.fallbackDecimals
                  );
                }
              }

              if (
                last.epoch
              ) {
                setCurrentTickEpoch(
                  last.epoch
                );
              }
            }

            setChartLoading(
              false
            );

            return;
          }
        }
      );

    /* -----------------------------------------------
       SUBSCRIBE ALL MARKETS
    ----------------------------------------------- */

    MARKETS.forEach(
      (market) => {
        ForexWebSocket.ticks(
          market.symbol
        );
      }
    );

    return () => {
      mountedRef.current =
        false;

      unsubscribe();

      /*
       * close() hapa ni safe kwa sababu
       * component ikirudi mount,
       * connect() itaanzisha socket tena.
       */
      ForexWebSocket.close();
    };
  }, []);

  /* =======================================================
     HISTORY WHEN MARKET CHANGES
  ======================================================= */

  useEffect(() => {
    const market =
      MARKETS.find(
        (item) =>
          item.id ===
          selectedMarketId
      ) ||
      MARKETS[0];

    selectedMarketRef.current =
      selectedMarketId;

    setChartLoading(true);

    setChartData([]);

    const existingLive =
      liveMarketsRef.current[
        market.symbol
      ];

    if (
      existingLive &&
      Number.isFinite(
        Number(
          existingLive.price
        )
      )
    ) {
      setPrice(
        Number(
          existingLive.price
        )
      );

      setPreviousPrice(
        null
      );

      setSessionStartPrice(
        existingLive.sessionStartPrice ??
          Number(
            existingLive.price
          )
      );

      previousLivePriceRef.current =
        Number(
          existingLive.price
        );

      if (
        existingLive.pipSize !==
          null &&
        existingLive.pipSize !==
          undefined
      ) {
        setPricePipSize(
          getDecimalPlaces(
            existingLive.pipSize,
            market.fallbackDecimals
          )
        );
      } else {
        setPricePipSize(
          market.fallbackDecimals
        );
      }

      if (
        existingLive.epoch
      ) {
        setCurrentTickEpoch(
          existingLive.epoch
        );
      }

      if (
        existingLive.tickId
      ) {
        setCurrentTickId(
          existingLive.tickId
        );
      }
    } else {
      setPrice(null);

      setPreviousPrice(
        null
      );

      setSessionStartPrice(
        null
      );

      setCurrentTickEpoch(
        null
      );

      setCurrentTickId(
        null
      );

      previousLivePriceRef.current =
        null;

      setPricePipSize(
        market.fallbackDecimals
      );
    }

    historyRequestRef.current =
      null;

    let cancelled = false;
    let retryTimer = null;

    const requestHistory =
      () => {
        if (cancelled) {
          return;
        }

        const requestId =
          ForexWebSocket.history(
            market.symbol,
            100
          );

        if (requestId) {
          historyRequestRef.current =
            requestId;

          return;
        }

        retryTimer =
          setTimeout(
            requestHistory,
            500
          );
      };

    requestHistory();

    return () => {
      cancelled = true;

      if (retryTimer) {
        clearTimeout(
          retryTimer
        );
      }
    };
  }, [selectedMarketId]);

  /* =======================================================
     MARKET SELECT
  ======================================================= */

  const handleMarketSelect =
    (marketId) => {
      selectedMarketRef.current =
        marketId;

      setSelectedMarketId(
        marketId
      );

      setMarketOpen(false);

      setVolatilityNav(
        "market"
      );
    };

  /* =======================================================
     AMOUNT
  ======================================================= */

  const increaseAmount =
    () => {
      setAmount(
        (value) =>
          Number(value) + 1
      );
    };

  const decreaseAmount =
    () => {
      setAmount(
        (value) =>
          Math.max(
            0,
            Number(value) - 1
          )
      );
    };

  const selectAmount =
    (value) => {
      const nextAmount =
        Number(value) || 0;

      setAmount(
        nextAmount
      );

      if (
        tradingMode ===
          "auto" &&
        currentStake <= 0
      ) {
        setCurrentStake(
          nextAmount
        );
      }
    };

  /* =======================================================
     TRADER TAB
  ======================================================= */

  const changeTraderTab =
    (tab) => {
      setTraderTab(tab);

      if (
        tab === "auto"
      ) {
        setTradingMode(
          "auto"
        );

        if (
          Number(
            currentStake
          ) <= 0
        ) {
          setCurrentStake(
            Number(amount) >
              0
              ? Number(amount)
              : 1
          );
        }
      } else {
        setTradingMode(
          "manual"
        );
      }
    };

  /* =======================================================
     START TRADE
  ======================================================= */

  const startTrade =
    async (type) => {
      try {
        const stake =
          tradingMode ===
            "auto" &&
          Number(
            currentStake
          ) > 0
            ? Number(
                currentStake
              )
            : Number(amount);

        if (
          !Number.isFinite(
            stake
          ) ||
          stake <= 0
        ) {
          alert(
            "Chagua amount/stake kwanza."
          );

          return;
        }

        if (
          tradeCount >=
          Number(maxTrades)
        ) {
          alert(
            "Maximum trades reached."
          );

          return;
        }

        const currentUsername =
          getCurrentUsername();

        if (!currentUsername) {
          alert(
            "Username haipo kwenye localStorage."
          );

          return;
        }

        const user =
          await getUserAccount();

        if (!user) {
          alert(
            "User account haijapatikana."
          );

          return;
        }

        setUsername(
          user.username
        );

        const currentPrice =
          Number(
            displayedPrice
          );

        if (
          !Number.isFinite(
            currentPrice
          )
        ) {
          alert(
            "Subiri Deriv live price ipatikane kwanza."
          );

          return;
        }

        const orderRef =
          doc(
            collection(
              db,
              "volatilityorder"
            )
          );

        const userRef =
          doc(
            db,
            "users",
            user.id
          );

        const tradeId =
          `${Date.now()}-${Math.random()
            .toString(36)
            .slice(2)}`;

        const entryPrice =
          currentPrice;

        const entryDigit =
          selectedDigit;

        const contractLabel =
          CONTRACTS.find(
            (item) =>
              item.id ===
              contractType
          )?.label ||
          contractType;

        const entryTick =
          currentTickEpoch ||
          Math.floor(
            Date.now() / 1000
          );

        let finalBalance =
          0;

        await runTransaction(
          db,
          async (
            transaction
          ) => {
            const userSnapshot =
              await transaction.get(
                userRef
              );

            if (
              !userSnapshot.exists()
            ) {
              throw new Error(
                "USER_NOT_FOUND"
              );
            }

            const userData =
              userSnapshot.data();

            const firestoreBalance =
              Number(
                userData
                  .usdtBalanceOther
              ) || 0;

            if (
              firestoreBalance <
              stake
            ) {
              throw new Error(
                "INSUFFICIENT_BALANCE"
              );
            }

            finalBalance =
              Number(
                (
                  firestoreBalance -
                  stake
                ).toFixed(2)
              );

            transaction.update(
              userRef,
              {
                usdtBalanceOther:
                  finalBalance,
              }
            );

            transaction.set(
              orderRef,
              {
                username:
                  currentUsername,

                userId:
                  user.id,

                localTradeId:
                  tradeId,

                orderType:
                  "VOLATILITY",

                status:
                  "OPEN",

                tradeType:
                  type,

                contractType:
                  contractType,

                contractLabel:
                  contractLabel,

                marketId:
                  selectedMarket.id,

                marketSymbol:
                  selectedMarket.symbol,

                marketName:
                  selectedMarket.name,

                stake:
                  Number(
                    stake.toFixed(2)
                  ),

                payout: 0,

                profit: 0,

                result: 0,

                entryPrice:
                  entryPrice,

                entryDigit:
                  entryDigit,

                entryTick:
                  entryTick,

                entryTickId:
                  currentTickId ||
                  null,

                exitPrice:
                  null,

                exitDigit:
                  null,

                exitTick:
                  null,

                exitTickId:
                  null,

                balanceBefore:
                  Number(
                    firestoreBalance.toFixed(
                      2
                    )
                  ),

                balanceAfter:
                  finalBalance,

                targetProfit:
                  Number(
                    targetProfit
                  ),

                stopLoss:
                  Number(
                    stopLoss
                  ),

                multiplier:
                  Number(
                    multiplier
                  ),

                resetOnWin:
                  Boolean(
                    resetOnWin
                  ),

                onWinReset:
                  Number(
                    onWinReset
                  ),

                maxTrades:
                  Number(
                    maxTrades
                  ),

                tradingMode:
                  tradingMode,

                traderTab:
                  traderTab,

                currency:
                  "USDT",

                entryEpoch:
                  currentTickEpoch ||
                  null,

                entryPriceDecimals:
                  getDecimalPlaces(
                    pricePipSize,
                    selectedMarket.fallbackDecimals
                  ),

                createdAt:
                  serverTimestamp(),

                updatedAt:
                  serverTimestamp(),

                closedAt:
                  null,
              }
            );
          }
        );

        setBalance(
          finalBalance
        );

        setTradeCount(
          (value) =>
            value + 1
        );

        setTradeType(
          type
        );

        setIsRunning(
          true
        );

        setCurrentStake(
          stake
        );

        setHistoryTab(
          "open"
        );

        setVolatilityNav(
          "history"
        );
      } catch (error) {
        console.error(
          "Start trade error:",
          error
        );

        if (
          error.message ===
          "INSUFFICIENT_BALANCE"
        ) {
          alert(
            "USDT balance haitoshi."
          );
          return;
        }

        if (
          error.message ===
          "USER_NOT_FOUND"
        ) {
          alert(
            "User account haijapatikana."
          );
          return;
        }

        alert(
          "Trade haikufunguka. Jaribu tena."
        );
      }
    };

  /* =======================================================
     STOP TRADING

     IMPORTANT:
     HAIWEKI ORDER LOST.
  ======================================================= */

  const stopTrading =
    () => {
      setIsRunning(false);
      setTradeType(null);
    };

  /* =======================================================
     CLOSE TRADE
  ======================================================= */

  const closeTrade =
    async (
      trade,
      won
    ) => {
      try {
        if (!trade) {
          return;
        }

        const orderId =
          trade.firestoreOrderId ||
          trade.id;

        if (!orderId) {
          alert(
            "Order ID haipo."
          );

          return;
        }

        const currentUsername =
          getCurrentUsername();

        if (!currentUsername) {
          alert(
            "Username haipo."
          );

          return;
        }

        const user =
          await getUserAccount();

        if (!user) {
          alert(
            "User account haijapatikana."
          );

          return;
        }

        const userRef =
          doc(
            db,
            "users",
            user.id
          );

        const orderRef =
          doc(
            db,
            "volatilityorder",
            orderId
          );

        const stake =
          Number(
            trade.stake
          ) || 0;

        const payout =
          won
            ? Number(
                (
                  stake *
                  1.98
                ).toFixed(2)
              )
            : 0;

        const profit =
          won
            ? Number(
                (
                  stake *
                  0.98
                ).toFixed(2)
              )
            : Number(
                (
                  -stake
                ).toFixed(2)
              );

        /* -----------------------------------------------
           USE TRADE'S MARKET
        ----------------------------------------------- */

        const tradeMarket =
          MARKETS.find(
            (market) =>
              market.symbol ===
              trade.marketSymbol
          ) ||
          selectedMarket;

        const tradeLiveMarket =
          liveMarkets[
            tradeMarket.symbol
          ] || null;

        const exitPriceCandidate =
          tradeLiveMarket?.price ??
          (
            trade.marketSymbol ===
            selectedMarket.symbol
              ? displayedPrice
              : null
          );

        const exitPrice =
          Number.isFinite(
            Number(
              exitPriceCandidate
            )
          )
            ? Number(
                exitPriceCandidate
              )
            : Number(
                trade.entryPrice
              );

        const exitPipSize =
          tradeLiveMarket?.pipSize ??
          (
            trade.marketSymbol ===
            selectedMarket.symbol
              ? pricePipSize
              : tradeMarket.fallbackDecimals
          );

        const exitDigit =
          getLastDigit(
            exitPrice,
            exitPipSize,
            tradeMarket.fallbackDecimals
          );

        const exitTick =
          tradeLiveMarket?.epoch ||
          (
            trade.marketSymbol ===
            selectedMarket.symbol
              ? currentTickEpoch
              : null
          ) ||
          Math.floor(
            Date.now() / 1000
          );

        const exitTickId =
          tradeLiveMarket?.tickId ||
          (
            trade.marketSymbol ===
            selectedMarket.symbol
              ? currentTickId
              : null
          ) ||
          null;

        let finalBalance =
          0;

        let alreadyClosed =
          false;

        await runTransaction(
          db,
          async (
            transaction
          ) => {
            const orderSnapshot =
              await transaction.get(
                orderRef
              );

            if (
              !orderSnapshot.exists()
            ) {
              throw new Error(
                "ORDER_NOT_FOUND"
              );
            }

            const orderData =
              orderSnapshot.data();

            /*
             * ONLY OPEN CAN CLOSE.
             */

            if (
              orderData.status !==
              "OPEN"
            ) {
              alreadyClosed =
                true;

              return;
            }

            const userSnapshot =
              await transaction.get(
                userRef
              );

            if (
              !userSnapshot.exists()
            ) {
              throw new Error(
                "USER_NOT_FOUND"
              );
            }

            const userData =
              userSnapshot.data();

            const firestoreBalance =
              Number(
                userData
                  .usdtBalanceOther
              ) || 0;

            finalBalance =
              won
                ? Number(
                    (
                      firestoreBalance +
                      payout
                    ).toFixed(2)
                  )
                : Number(
                    firestoreBalance.toFixed(
                      2
                    )
                  );

            /*
             * WIN:
             * payout inaongezwa.
             *
             * LOSS:
             * stake tayari ilikatwa.
             */

            if (won) {
              transaction.update(
                userRef,
                {
                  usdtBalanceOther:
                    finalBalance,
                }
              );
            }

            transaction.update(
              orderRef,
              {
                status:
                  won
                    ? "WON"
                    : "LOST",

                result:
                  profit,

                profit:
                  profit,

                payout:
                  payout,

                exitPrice:
                  exitPrice,

                exitDigit:
                  exitDigit,

                exitTick:
                  exitTick,

                exitTickId:
                  exitTickId,

                exitEpoch:
                  exitTick,

                exitPriceDecimals:
                  getDecimalPlaces(
                    exitPipSize,
                    tradeMarket.fallbackDecimals
                  ),

                balanceBeforeClose:
                  firestoreBalance,

                balanceAfterClose:
                  finalBalance,

                closedAt:
                  serverTimestamp(),

                updatedAt:
                  serverTimestamp(),
              }
            );
          }
        );

        if (alreadyClosed) {
          return;
        }

        setBalance(
          finalBalance
        );

        if (won) {
          setTotalProfit(
            (value) =>
              value + profit
          );

          if (resetOnWin) {
            const nextStake =
              Number(amount) >
              0
                ? Number(
                    amount
                  ) *
                  Number(
                    onWinReset
                  )
                : stake;

            setCurrentStake(
              Math.max(
                1,
                nextStake
              )
            );
          } else {
            setCurrentStake(
              Math.max(
                1,
                Number(
                  amount
                ) || stake
              )
            );
          }
        } else {
          setTotalLoss(
            (value) =>
              value +
              Math.abs(
                profit
              )
          );

          /*
           * LOSS:
           * current stake × multiplier
           */

          setCurrentStake(
            Math.max(
              1,
              stake *
                Number(
                  multiplier
                )
            )
          );
        }

        const remainingOpen =
          orders.filter(
            (item) =>
              item.id !==
                orderId &&
              item.status ===
                "OPEN"
          );

        if (
          remainingOpen.length ===
          0
        ) {
          setIsRunning(
            false
          );

          setTradeType(
            null
          );
        }

        setHistoryTab(
          "closed"
        );

        setVolatilityNav(
          "history"
        );
      } catch (error) {
        console.error(
          "Close trade error:",
          error
        );

        if (
          error.message ===
          "ORDER_NOT_FOUND"
        ) {
          alert(
            "Order haijapatikana Firestore."
          );

          return;
        }

        if (
          error.message ===
          "USER_NOT_FOUND"
        ) {
          alert(
            "User account haijapatikana."
          );

          return;
        }

        alert(
          "Trade haikufungwa vizuri."
        );
      }
    };

  /* =======================================================
     STOP CONDITIONS
  ======================================================= */

  useEffect(() => {
    if (!isRunning) {
      return;
    }

    if (
      totalProfit >=
      Number(
        targetProfit
      )
    ) {
      stopTrading();
      return;
    }

    if (
      totalLoss >=
      Number(
        stopLoss
      )
    ) {
      stopTrading();
      return;
    }

    if (
      tradeCount >=
      Number(
        maxTrades
      )
    ) {
      stopTrading();
    }
  }, [
    totalProfit,
    totalLoss,
    tradeCount,
    targetProfit,
    stopLoss,
    maxTrades,
    isRunning,
  ]);

  /* =======================================================
     BOTTOM NAV
  ======================================================= */

  const handleBottomNavChange =
    (tab) => {
      setVolatilityNav(tab);

      if (
        tab === "market"
      ) {
        setMarketOpen(true);

        setTimeout(() => {
          document
            .querySelector(
              ".volatility-card"
            )
            ?.scrollIntoView({
              behavior:
                "smooth",
              block:
                "start",
            });
        }, 50);

        return;
      }

      if (
        tab === "history"
      ) {
        setTimeout(() => {
          document
            .querySelector(
              ".trade-history"
            )
            ?.scrollIntoView({
              behavior:
                "smooth",
              block:
                "start",
            });
        }, 50);
      }
    };

  /* =======================================================
     HISTORY NAV
  ======================================================= */

  const handleHistoryNavChange =
    (tab) => {
      setHistoryTab(tab);

      setVolatilityNav(
        "history"
      );

      setTimeout(() => {
        document
          .querySelector(
            ".trade-history"
          )
          ?.scrollIntoView({
            behavior:
              "smooth",
            block:
              "start",
          });
      }, 50);
    };

  /* =======================================================
     TOOLTIP
  ======================================================= */

  const CustomTooltip =
    ({
      active,
      payload,
    }) => {
      if (
        !active ||
        !payload ||
        !payload.length
      ) {
        return null;
      }

      return (
        <div className="volatility-tooltip">
          <strong>
            {formatPrice(
              payload[0].value
            )}
          </strong>
        </div>
      );
    };

  /* =======================================================
     UI VALUES
  ======================================================= */

  const gradientId =
    `volatilityGradient-${selectedMarketId}`;

  const userInitial =
    username
      ? username
          .charAt(0)
          .toUpperCase()
      : "D";

  const connectionLabel =
    wsStatus ===
    "connected"
      ? "LIVE"
      : wsStatus ===
        "connecting"
      ? "CONNECTING"
      : "OFFLINE";

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="volatility-page">
      {/* HEADER */}

      <VolatilityHeader/>
      
      {/* DERIV STATUS */}

      <div
        className={`volatility-live-status ${wsStatus}`}
      >
        <span className="volatility-live-dot" />

        <span>
          Deriv{" "}
          {connectionLabel}
        </span>

        {wsStatus ===
          "connected" &&
          currentTickEpoch && (
            <span>
              {" "}
              ·{" "}
              {formatTickTime(
                currentTickEpoch
              )}
            </span>
          )}
      </div>

      {wsError && (
        <div className="volatility-ws-error">
          {wsError}
        </div>
      )}

      {/* TRADER TABS */}

      <div className="trader-tabs">
        <button
          type="button"
          className={
            traderTab ===
            "manual"
              ? "trader-tab active"
              : "trader-tab"
          }
          onClick={() =>
            changeTraderTab(
              "manual"
            )
          }
        >
          Manual Trader
        </button>

        <button
          type="button"
          className={
            traderTab ===
            "bulk"
              ? "trader-tab active"
              : "trader-tab"
          }
          onClick={() =>
            changeTraderTab(
              "bulk"
            )
          }
        >
          Bulk Trader
        </button>

        <button
          type="button"
          className={
            traderTab ===
            "auto"
              ? "trader-tab active"
              : "trader-tab"
          }
          onClick={() =>
            changeTraderTab(
              "auto"
            )
          }
        >
          Auto Trader
        </button>
      </div>

      {/* CONTRACT TABS */}

      <div className="contract-tabs">
        {CONTRACTS.map(
          (contract) => (
            <button
              type="button"
              key={
                contract.id
              }
              className={
                contractType ===
                contract.id
                  ? "contract-tab active"
                  : "contract-tab"
              }
              onClick={() =>
                setContractType(
                  contract.id
                )
              }
            >
              {
                contract.label
              }
            </button>
          )
        )}
      </div>

      {/* MAIN CARD */}

      <div className="volatility-card">
        {/* MARKET HEADER */}

        <div className="volatility-header">
          <div className="volatility-title">
            <div className="volatility-icon">
              <span />
              <span />
              <span />
            </div>

            <div className="volatility-title-info">
              <h3>
                {
                  selectedMarket.name
                }
              </h3>

              <div className="volatility-price-row">
                <span className="volatility-price">
                  {displayedPrice ===
                  null
                    ? "Loading..."
                    : formatPrice(
                        displayedPrice
                      )}
                </span>
              </div>
            </div>

            <div
              className={
                marketSessionPercentage >=
                0
                  ? "volatility-change-top positive"
                  : "volatility-change-top negative"
              }
            >
              {
                percentageText
              }
            </div>
          </div>

          <button
            type="button"
            className={
              marketOpen
                ? "volatility-dropdown open"
                : "volatility-dropdown"
            }
            onClick={() =>
              setMarketOpen(
                (previous) =>
                  !previous
              )
            }
            aria-label="Change market"
            aria-expanded={
              marketOpen
            }
          >
            <span className="dropdown-chevron">
              ▼
            </span>
          </button>
        </div>

        {/* MARKET DROPDOWN */}

        {marketOpen && (
          <div className="volatility-market-menu">
            <div className="market-menu-title">
              DERIVED
            </div>

            {marketValues.map(
              (market) => {
                const isSelected =
                  market.id ===
                  selectedMarketId;

                const marketChange =
                  market.displayChange;

                const activeSymbol =
                  activeSymbols[
                    market.symbol
                  ];

                return (
                  <button
                    type="button"
                    key={
                      market.id
                    }
                    className={
                      isSelected
                        ? "market-option active"
                        : "market-option"
                    }
                    onClick={() =>
                      handleMarketSelect(
                        market.id
                      )
                    }
                  >
                    <span
                      className={
                        marketChange >=
                        0
                          ? "market-status-dot green"
                          : "market-status-dot red"
                      }
                    />

                    <span className="market-option-name">
                      {activeSymbol?.displayName ||
                        market.name}
                    </span>

                    <span className="market-option-price">
                      {market.displayPrice ===
                      null
                        ? "—"
                        : formatMarketPrice(
                            market.displayPrice,
                            market.pipSize,
                            market.fallbackDecimals
                          )}
                    </span>

                    <span
                      className={
                        marketChange >=
                        0
                          ? "market-option-change green"
                          : "market-option-change red"
                      }
                    >
                      {market.displayPrice ===
                      null
                        ? "—"
                        : marketChange >=
                          0
                        ? `▲ +${marketChange.toFixed(
                            2
                          )}%`
                        : `▼ ${marketChange.toFixed(
                            2
                          )}%`}
                    </span>
                  </button>
                );
              }
            )}
          </div>
        )}

        {/* CHART */}

        <div className="volatility-chart">
          {chartLoading && (
            <div className="volatility-chart-loading">
              
              ...
            </div>
          )}

          <ResponsiveContainer
            width="100%"
            height="100%"
          >
            <AreaChart
              data={
                chartData
              }
              margin={{
                top: 8,
                right: 5,
                left: 0,
                bottom: 3,
              }}
            >
              <defs>
                <linearGradient
                  id={
                    gradientId
                  }
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="0%"
                    stopColor="#20d56a"
                    stopOpacity={
                      0.25
                    }
                  />

                  <stop
                    offset="100%"
                    stopColor="#20d56a"
                    stopOpacity={
                      0
                    }
                  />
                </linearGradient>
              </defs>

              <CartesianGrid
                stroke="#202326"
                strokeDasharray="2 4"
                vertical={
                  false
                }
              />

              <XAxis
                dataKey="time"
                hide
              />

              <YAxis
                orientation="right"
                domain={[
                  "dataMin",
                  "dataMax",
                ]}
                tick={{
                  fill: "#676d75",
                  fontSize: 8,
                }}
                axisLine={
                  false
                }
                tickLine={
                  false
                }
                width={55}
                tickFormatter={(
                  value
                ) =>
                  formatPrice(
                    value
                  )
                }
              />

              <Tooltip
                content={
                  <CustomTooltip />
                }
              />

              {displayedPrice !==
                null && (
                <ReferenceLine
                  y={
                    displayedPrice
                  }
                  stroke="#22d66b"
                  strokeDasharray="3 3"
                  strokeOpacity={
                    0.25
                  }
                />
              )}

              <Area
                type="monotone"
                dataKey="price"
                stroke="#20d56a"
                strokeWidth={
                  1.5
                }
                fill={`url(#${gradientId})`}
                dot={false}
                activeDot={{
                  r: 3,
                  fill: "#20d56a",
                  stroke:
                    "#07150d",
                  strokeWidth: 1,
                }}
                isAnimationActive={
                  false
                }
              />
            </AreaChart>
          </ResponsiveContainer>

          {displayedPrice !==
            null && (
            <div
              className={
                marketSessionPercentage >=
                0
                  ? "current-price-badge positive"
                  : "current-price-badge negative"
              }
            >
              <span className="current-price-value">
                {formatPrice(
                  displayedPrice
                )}
              </span>

              <span className="current-price-percent">
                {marketSessionPercentage >=
                0
                  ? `+${marketSessionPercentage.toFixed(
                      2
                    )}%`
                  : `${marketSessionPercentage.toFixed(
                      2
                    )}%`}
              </span>
            </div>
          )}
        </div>

        {/* DIGITS */}

        <div className="volatility-digits">
          {DIGITS.map(
            (item) => {
              const active =
                selectedDigit ===
                item.number;

              return (
                <button
                  type="button"
                  key={
                    item.number
                  }
                  className={
                    active
                      ? "digit-item active"
                      : "digit-item"
                  }
                  onClick={() => {}}
                >
                  <span className="digit-circle">
                    {
                      item.number
                    }
                  </span>

                  <span className="digit-percent">
                    {
                      item.percent
                    }
                  </span>

                  {active && (
                    <span className="digit-arrow">
                      ▼
                    </span>
                  )}
                </button>
              );
            }
          )}
        </div>

        {/* LIVE TICK */}

        <div className="volatility-live-tick">
          <span>
            LIVE TICK
          </span>

          <strong>
            {displayedPrice ===
            null
              ? "—"
              : formatPrice(
                  displayedPrice
                )}
          </strong>

          <span>
            Digit{" "}
            {
              selectedDigit
            }
          </span>

          {currentTickEpoch && (
            <span>
              {formatTickTime(
                currentTickEpoch
              )}
            </span>
          )}
        </div>

        {/* AUTO / MANUAL */}

        <div className="trading-mode">
          <button
            type="button"
            className={
              tradingMode ===
              "auto"
                ? "trading-mode-button active"
                : "trading-mode-button"
            }
            onClick={() => {
              setTradingMode(
                "auto"
              );

              setTraderTab(
                "auto"
              );

              if (
                Number(
                  currentStake
                ) <= 0
              ) {
                setCurrentStake(
                  Number(
                    amount
                  ) > 0
                    ? Number(
                        amount
                      )
                    : 1
                );
              }
            }}
          >
            AUTO
          </button>

          <button
            type="button"
            className={
              tradingMode ===
              "manual"
                ? "trading-mode-button active"
                : "trading-mode-button"
            }
            onClick={() => {
              setTradingMode(
                "manual"
              );

              setTraderTab(
                "manual"
              );
            }}
          >
            MANUAL
          </button>
        </div>

        {/* AMOUNT */}

        <div className="amount-panel">
          <div className="amount-label">
            AMOUNT
          </div>

          <div className="amount-control">
            <button
              type="button"
              onClick={
                decreaseAmount
              }
            >
              −
            </button>

            <div className="amount-value">
              <span>$</span>

              <strong>
                {amount}
              </strong>
            </div>

            <button
              type="button"
              onClick={
                increaseAmount
              }
            >
              +
            </button>
          </div>

          <div className="amount-presets">
            {AMOUNT_PRESETS.map(
              (value) => (
                <button
                  type="button"
                  key={
                    value
                  }
                  className={
                    amount ===
                    value
                      ? "amount-preset active"
                      : "amount-preset"
                  }
                  onClick={() =>
                    selectAmount(
                      value
                    )
                  }
                >
                  ${value}
                </button>
              )
            )}
          </div>
        </div>

        {/* AUTO SETTINGS */}

        {tradingMode ===
          "auto" && (
          <>
            <div className="auto-settings">
              <div className="setting-card profit">
                <span>
                  TARGET PROFIT
                </span>

                <input
                  type="number"
                  min="1"
                  step="1"
                  value={
                    targetProfit
                  }
                  onChange={(
                    event
                  ) =>
                    setTargetProfit(
                      Math.max(
                        1,
                        Number(
                          event
                            .target
                            .value
                        ) ||
                          1
                      )
                    )
                  }
                />
              </div>

              <div className="setting-card loss">
                <span>
                  STOP LOSS
                </span>

                <input
                  type="number"
                  min="1"
                  step="1"
                  value={
                    stopLoss
                  }
                  onChange={(
                    event
                  ) =>
                    setStopLoss(
                      Math.max(
                        1,
                        Number(
                          event
                            .target
                            .value
                        ) ||
                          1
                      )
                    )
                  }
                />
              </div>

              <div className="setting-card multiplier">
                <span>
                  MULTIPLIER
                </span>

                <input
                  type="number"
                  min="1"
                  step="0.1"
                  value={
                    multiplier
                  }
                  onChange={(
                    event
                  ) =>
                    setMultiplier(
                      Math.max(
                        1,
                        Number(
                          event
                            .target
                            .value
                        ) ||
                          1
                      )
                    )
                  }
                />
              </div>
            </div>

            <div className="stake-scaling">
              <div className="stake-header">
                <span>
                  STAKE SCALING
                </span>

                <label>
                  <input
                    type="checkbox"
                    checked={
                      resetOnWin
                    }
                    onChange={(
                      event
                    ) =>
                      setResetOnWin(
                        event
                          .target
                          .checked
                      )
                    }
                  />

                  <span>
                    Reset on win
                  </span>
                </label>
              </div>

              <div className="scaling-fields">
                <div className="scaling-field">
                  <label>
                    ON WIN (RESET)
                  </label>

                  <div>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={
                        onWinReset
                      }
                      onChange={(
                        event
                      ) =>
                        setOnWinReset(
                          Math.max(
                            1,
                            Number(
                              event
                                .target
                                .value
                            ) ||
                              1
                          )
                        )
                      }
                    />

                    <span>
                      x
                    </span>
                  </div>
                </div>

                <div className="scaling-field">
                  <label>
                    MAX TRADES
                  </label>

                  <div>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={
                        maxTrades
                      }
                      onChange={(
                        event
                      ) =>
                        setMaxTrades(
                          Math.max(
                            1,
                            Number(
                              event
                                .target
                                .value
                            ) ||
                              1
                          )
                        )
                      }
                    />
                  </div>
                </div>

                <div className="scaling-field">
                  <label>
                    CURRENT STAKE
                  </label>

                  <div>
                    <span className="scaling-value">
                      $
                      {Number(
                        displayStake
                      ).toFixed(
                        2
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <p className="scaling-description">
                Martingale:
                after a loss
                the stake ×
                multiplier,
                then resets
                after a win.
              </p>
            </div>
          </>
        )}

        {/* TRADE BUTTONS */}

        <div className="trade-buttons">
          <button
            type="button"
            className="trade-button even"
            onClick={() =>
              startTrade(
                "Even"
              )
            }
            disabled={
              balanceLoading ||
              displayedPrice ===
                null
            }
          >
            <div className="trade-button-top">
              <strong>
                {isRunning &&
                tradeType ===
                  "Even"
                  ? "Running · Even"
                  : "Start · Even"}
              </strong>

              <span>
                98%
              </span>
            </div>

            <div className="trade-payout">
              <AnimatedMoney
                value={
                  activePayout
                }
              />
            </div>
          </button>

          <button
            type="button"
            className="trade-button odd"
            onClick={() =>
              startTrade(
                "Odd"
              )
            }
            disabled={
              balanceLoading ||
              displayedPrice ===
                null
            }
          >
            <div className="trade-button-top">
              <strong>
                {isRunning &&
                tradeType ===
                  "Odd"
                  ? "Running · Odd"
                  : "Start · Odd"}
              </strong>

              <span>
                98%
              </span>
            </div>

            <div className="trade-payout">
              <AnimatedMoney
                value={
                  activePayout
                }
              />
            </div>
          </button>
        </div>

        {/* OPEN TRADE CONTROLS */}

        {openTrades.length >
          0 && (
          <div className="demo-trade-controls">
            {openTrades[0] && (
              <>
                <button
                  type="button"
                  onClick={() =>
                    closeTrade(
                      openTrades[0],
                      true
                    )
                  }
                >
                  Real Win
                </button>

                <button
                  type="button"
                  onClick={() =>
                    closeTrade(
                      openTrades[0],
                      false
                    )
                  }
                >
                  Real Loss
                </button>
              </>
            )}

            <button
              type="button"
              onClick={
                stopTrading
              }
            >
              Stop
            </button>
          </div>
        )}

        {/* BALANCE */}

        <div className="balance-display">
          <span className="balance-label">
            Balance
          </span>

          <span className="balance-value">
            {balanceLoading
              ? "Loading..."
              : `$${balance.toFixed(
                  2
                )}`}
          </span>
        </div>

        {/* HISTORY */}

        <div className="trade-history">
          <div className="history-tabs">
            <button
              type="button"
              className={
                historyTab ===
                "open"
                  ? "history-tab active"
                  : "history-tab"
              }
              onClick={() => {
                setHistoryTab(
                  "open"
                );

                setVolatilityNav(
                  "history"
                );
              }}
            >
              Open (
              {
                openTrades.length
              }
              )
            </button>

            <button
              type="button"
              className={
                historyTab ===
                "closed"
                  ? "history-tab active"
                  : "history-tab"
              }
              onClick={() => {
                setHistoryTab(
                  "closed"
                );

                setVolatilityNav(
                  "history"
                );
              }}
            >
              Closed (
              {
                closedTrades.length
              }
              )
            </button>

            <button
              type="button"
              className={
                historyTab ===
                "transactions"
                  ? "history-tab active"
                  : "history-tab"
              }
              onClick={() => {
                setHistoryTab(
                  "transactions"
                );

                setVolatilityNav(
                  "history"
                );
              }}
            >
              Transactions
            </button>

            <button
              type="button"
              className="history-add"
              onClick={() => {
                setHistoryTab(
                  "transactions"
                );

                setVolatilityNav(
                  "history"
                );
              }}
              aria-label="View transactions"
            >
              +
            </button>
          </div>

          <div className="history-content">
            {/* OPEN */}

            {historyTab ===
              "open" && (
              <>
                {ordersLoading ? (
                  <div className="history-empty">
                    Loading open
                    trades...
                  </div>
                ) : openTrades.length ===
                  0 ? (
                  <div className="history-empty">
                    No open
                    predictions

                    <small>
                      Your active
                      predictions
                      will appear
                      here
                    </small>
                  </div>
                ) : (
                  openTrades.map(
                    (trade) => (
                      <div
                        className="trade-row status-open"
                        key={
                          trade.id
                        }
                      >
                        <div>
                          <div className="trade-row-title">
                            {trade.tradeType ||
                              trade.type}
                          </div>

                          <div className="trade-row-time">
                            Stake $
                            {Number(
                              trade.stake
                            ).toFixed(
                              2
                            )}
                          </div>

                          <div className="trade-row-time">
                            {trade.entryTick
                              ? `tick ${trade.entryTick} → —`
                              : `Entry ${formatMarketPrice(
                                  trade.entryPrice,
                                  trade.entryPriceDecimals,
                                  2
                                )}`}
                          </div>

                          <div className="trade-row-time">
                            {
                              trade.marketName
                            }
                          </div>

                          <div className="trade-row-time">
                            Digit{" "}
                            {
                              trade.entryDigit
                            }
                          </div>
                        </div>

                        <div className="trade-open">
                          OPEN
                        </div>
                      </div>
                    )
                  )
                )}
              </>
            )}

            {/* CLOSED */}

            {historyTab ===
              "closed" && (
              <>
                {ordersLoading ? (
                  <div className="history-empty">
                    Loading closed
                    trades...
                  </div>
                ) : closedTrades.length ===
                  0 ? (
                  <div className="history-empty">
                    No closed
                    predictions
                  </div>
                ) : (
                  closedTrades.map(
                    (trade) => (
                      <div
                        className={
                          trade.status ===
                          "WON"
                            ? "trade-row status-won"
                            : "trade-row status-lost"
                        }
                        key={
                          trade.id
                        }
                      >
                        <div>
                          <div className="trade-row-title">
                            {trade.tradeType ||
                              trade.type}
                          </div>

                          <div className="trade-row-time">
                            Stake $
                            {Number(
                              trade.stake
                            ).toFixed(
                              2
                            )}
                          </div>

                          <div className="trade-row-time">
                            {trade.entryTick &&
                            trade.exitTick
                              ? `tick ${trade.entryTick} → ${trade.exitTick}`
                              : `Entry ${formatMarketPrice(
                                  trade.entryPrice,
                                  trade.entryPriceDecimals,
                                  2
                                )} → Exit ${formatMarketPrice(
                                  trade.exitPrice,
                                  trade.exitPriceDecimals,
                                  2
                                )}`}
                          </div>

                          <div className="trade-row-time">
                            Digit{" "}
                            {
                              trade.entryDigit
                            }
                            {" → "}
                            {
                              trade.exitDigit
                            }
                          </div>

                          <div className="trade-row-time">
                            {
                              trade.marketName
                            }
                          </div>
                        </div>

                        <div
                          className={
                            trade.status ===
                            "WON"
                              ? "trade-win"
                              : "trade-loss"
                          }
                        >
                          <div>
                            <strong>
                              {
                                trade.status
                              }
                            </strong>
                          </div>

                          <div>
                            {Number(
                              trade.result
                            ) >=
                            0
                              ? "+"
                              : "-"}
                            $
                            {Math.abs(
                              Number(
                                trade.result
                              )
                            ).toFixed(
                              2
                            )}
                          </div>

                          <div className="trade-row-time">
                            digit{" "}
                            {
                              trade.exitDigit
                            }
                          </div>
                        </div>
                      </div>
                    )
                  )
                )}
              </>
            )}

            {/* TRANSACTIONS */}

            {historyTab ===
              "transactions" && (
              <>
                {ordersLoading ? (
                  <div className="history-empty">
                    Loading
                    transactions...
                  </div>
                ) : transactions.length ===
                  0 ? (
                  <div className="history-empty">
                    No transactions
                  </div>
                ) : (
                  transactions.map(
                    (trade) => {
                      const isOpen =
                        trade.status ===
                        "OPEN";

                      const isWon =
                        trade.status ===
                        "WON";

                      const isLost =
                        trade.status ===
                        "LOST";

                      return (
                        <div
                          className={
                            isOpen
                              ? "trade-row status-open"
                              : isWon
                              ? "trade-row status-won"
                              : "trade-row status-lost"
                          }
                          key={
                            trade.id
                          }
                        >
                          <div>
                            <div className="trade-row-title">
                              {trade.tradeType ||
                                trade.type}
                            </div>

                            <div className="trade-row-time">
                              Stake $
                              {Number(
                                trade.stake
                              ).toFixed(
                                2
                              )}
                            </div>

                            <div className="trade-row-time">
                              {trade.entryTick &&
                              trade.exitTick
                                ? `tick ${trade.entryTick} → ${trade.exitTick}`
                                : trade.entryTick
                                ? `tick ${trade.entryTick} → —`
                                : `Entry ${formatMarketPrice(
                                    trade.entryPrice,
                                    trade.entryPriceDecimals,
                                    2
                                  )}`}
                            </div>

                            <div className="trade-row-time">
                              Digit{" "}
                              {
                                trade.entryDigit
                              }

                              {trade.exitDigit !==
                                null &&
                                trade.exitDigit !==
                                  undefined && (
                                  <>
                                    {" → "}
                                    {
                                      trade.exitDigit
                                    }
                                  </>
                                )}
                            </div>

                            <div className="trade-row-time">
                              {
                                trade.marketName
                              }
                            </div>
                          </div>

                          <div
                            className={
                              isWon
                                ? "trade-win"
                                : isLost
                                ? "trade-loss"
                                : "trade-open"
                            }
                          >
                            <div>
                              <strong>
                                {isOpen
                                  ? "OPEN"
                                  : isWon
                                  ? "WON"
                                  : "LOST"}
                              </strong>
                            </div>

                            {!isOpen && (
                              <div>
                                {Number(
                                  trade.result
                                ) >=
                                0
                                  ? "+"
                                  : "-"}
                                $
                                {Math.abs(
                                  Number(
                                    trade.result
                                  )
                                ).toFixed(
                                  2
                                )}
                              </div>
                            )}

                            {!isOpen &&
                              trade.exitDigit !==
                                null &&
                              trade.exitDigit !==
                                undefined && (
                                <div className="trade-row-time">
                                  digit{" "}
                                  {
                                    trade.exitDigit
                                  }
                                </div>
                              )}
                          </div>
                        </div>
                      );
                    }
                  )
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* BOTTOM NAV */}

      <BottomNavVolatility
        activeTab={
          volatilityNav
        }
        historyTab={
          historyTab
        }
        onTabChange={
          handleBottomNavChange
        }
        onHistoryChange={
          handleHistoryNavChange
        }
      />
    </div>
  );
};

export default Volatility;
